import { cn } from "@app/ui";
import { useRef } from "react";
import { pad } from "../../lib/format.js";
import { useSettingsStore } from "../../stores/settings.store.js";

interface ClockProps {
  mode: "hour" | "minute";
  /** hour mode (12h): index 0–11 (0 = 12 o'clock); hour mode (24h): actual hour
   *  0–23; minute mode: actual minute 0–59. */
  selected: number;
  /** Hour mode draws two rings (0–11 outer, 12–23 inner) like a 24-hour dial. */
  hours24?: boolean;
  onSelect: (value: number) => void;
  onDragEnd?: () => void;
  /** Rendered pixel size of the dial. Internal geometry uses the fixed viewBox,
   *  so the dial scales as a whole (e.g. +30% on mobile). Defaults to CLOCK_SIZE. */
  size?: number;
}

const CLOCK_SIZE = 180;
const CENTER = CLOCK_SIZE / 2;
const OUTER_RADIUS = 72;
const INNER_RADIUS = 46;
const HIT_RADIUS = 17;
const DRAG_THRESHOLD = 5;

export function ClockFace({ mode, selected, hours24, onSelect, onDragEnd, size = CLOCK_SIZE }: ClockProps) {
  const textScale = useSettingsStore((s) => s.textScale);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  const dragMoved = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const twoRing = mode === "hour" && hours24;

  const handInner = twoRing && selected >= 12;
  const handRadius = handInner ? INNER_RADIUS : OUTER_RADIUS;
  const handIndex = mode === "hour" ? selected % 12 : selected;
  const handPos = indexToPos(handIndex, mode === "hour" ? 12 : 60, handRadius);
  const exactDot = mode === "minute" && selected % 5 !== 0;

  // Build the dial labels: hour/24h = two rings, otherwise a single ring of 12.
  const marks: { value: number; index: number; radius: number }[] = [];
  if (twoRing) {
    for (let i = 0; i < 12; i++) marks.push({ value: i, index: i, radius: OUTER_RADIUS });
    for (let i = 0; i < 12; i++) marks.push({ value: i + 12, index: i, radius: INNER_RADIUS });
  } else if (mode === "hour") {
    for (let i = 0; i < 12; i++) marks.push({ value: i, index: i, radius: OUTER_RADIUS });
  } else {
    for (let i = 0; i < 12; i++) marks.push({ value: (i * 5) % 60, index: i, radius: OUTER_RADIUS });
  }

  function valueFromPointer(clientX: number, clientY: number, snapped: boolean): number {
    const rect = svgRef.current!.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    const angle = Math.atan2(dy, dx) + Math.PI / 2;
    const norm = ((angle / (2 * Math.PI)) % 1 + 1) % 1;
    if (mode === "hour") {
      const idx = Math.round(norm * 12) % 12;
      if (!twoRing) return idx;
      // Pick the ring by distance from center (viewBox units; SVG may be scaled).
      const scale = CLOCK_SIZE / rect.width;
      const distVb = Math.sqrt(dx * dx + dy * dy) * scale;
      const inner = distVb < (OUTER_RADIUS + INNER_RADIUS) / 2;
      return inner ? idx + 12 : idx;
    }
    // minute: click snaps to nearest 5-min mark; drag gives exact 0-59
    return snapped ? (Math.round(norm * 12) * 5) % 60 : Math.round(norm * 60) % 60;
  }

  function handlePointerDown(e: React.PointerEvent<SVGSVGElement>) {
    dragging.current = true;
    dragMoved.current = false;
    startPos.current = { x: e.clientX, y: e.clientY };
    svgRef.current?.setPointerCapture(e.pointerId);
    onSelect(valueFromPointer(e.clientX, e.clientY, mode === "minute"));
  }

  function handlePointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (!dragging.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) dragMoved.current = true;
    onSelect(valueFromPointer(e.clientX, e.clientY, mode === "minute" && !dragMoved.current));
  }

  function handlePointerUp(e: React.PointerEvent<SVGSVGElement>) {
    if (!dragging.current) return;
    dragging.current = false;
    onSelect(valueFromPointer(e.clientX, e.clientY, mode === "minute" && !dragMoved.current));
    onDragEnd?.();
  }

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${CLOCK_SIZE} ${CLOCK_SIZE}`}
      width={size}
      height={size}
      className="touch-none select-none cursor-pointer"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <defs>
        <linearGradient id="clockAccent" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={CLOCK_SIZE} y2={CLOCK_SIZE}>
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="55%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
        <radialGradient id="clockFaceSheen" cx="50%" cy="38%" r="70%">
          <stop offset="0%" stopColor="rgba(139,92,246,0.12)" />
          <stop offset="100%" stopColor="rgba(99,102,241,0.03)" />
        </radialGradient>
      </defs>

      <circle cx={CENTER} cy={CENTER} r={CENTER - 4} fill="hsl(var(--muted))" />
      <circle cx={CENTER} cy={CENTER} r={CENTER - 4} fill="url(#clockFaceSheen)" />

      <line x1={CENTER} y1={CENTER} x2={handPos.x} y2={handPos.y}
        stroke="url(#clockAccent)" strokeWidth={2.5} strokeLinecap="round" />
      <circle cx={CENTER} cy={CENTER} r={4} fill="url(#clockAccent)" />

      {marks.map(({ value, index, radius }) => {
        const { x, y } = indexToPos(index, 12, radius);
        const isSelected = value === selected;
        const label =
          mode === "hour"
            ? twoRing
              ? String(value)
              : String(value === 0 ? 12 : value)
            : pad(value);
        const selRadius = twoRing ? 14 : HIT_RADIUS - 1;
        return (
          <g key={`${radius}-${index}`} className={cn("group origin-center [transform-box:fill-box]", isSelected ? "scale-[1.2]" : "hover:scale-[1.15]")}>
            <circle cx={x} cy={y} r={HIT_RADIUS}
              className={isSelected ? "fill-transparent" : "fill-transparent group-hover:fill-accent transition-colors"} />
            {isSelected && <circle cx={x} cy={y} r={selRadius} fill="url(#clockAccent)" />}
            <text x={x} y={y} textAnchor="middle" dominantBaseline="central"
              fontSize={(mode === "hour" ? (twoRing ? 13 : 16) : 13) * textScale}
              fontFamily="var(--app-font-family)"
              fontWeight={isSelected ? 600 : 400}
              fill={isSelected ? "#ffffff" : "hsl(var(--foreground))"}
              style={{ pointerEvents: "none" }}>
              {label}
            </text>
          </g>
        );
      })}

      {exactDot && <circle cx={handPos.x} cy={handPos.y} r={7} fill="url(#clockAccent)" />}
    </svg>
  );
}

function indexToPos(i: number, total: number, radius: number): { x: number; y: number } {
  const angle = -Math.PI / 2 + (i / total) * 2 * Math.PI;
  return { x: CENTER + radius * Math.cos(angle), y: CENTER + radius * Math.sin(angle) };
}
